import type { HpSearchScopeDto } from '@zg-rentals/ts-hotpads-api-client';
import type {
  RecentSearchUpdateBody,
  SavedSearchCreateBody,
  SavedSearchFilter,
  SavedSearchItem,
} from '@/src/lib/api/endpoints/user';
// Deep-import the pure filter modules — the @/src/lib/filters barrel drags in
// client-only providers, and this module is reachable from Server Components.
import { deserializeFromUrl, serializeToUrl, toApiParams } from '@/src/lib/filters/filterSerializer';
import { toZillowIdFilterFields, zillowIdFilterField } from '@/src/lib/filters/searchScope';
import type { FilterState } from '@/src/lib/filters/filterTypes';
import { LISTINGS_PER_PAGE } from '@/src/lib/constants';

export type SavedSearchFrequency = 'instant' | 'daily' | 'weekly' | 'never';

// The backend represents the "instant" alert cadence as 'hourly'; all other values match the UI.
// Accepts a plain string so callers with backend-sourced values (e.g. email-link params) can reuse it.
export function toApiEmailFrequency(frequency: string): string {
  return frequency === 'instant' ? 'hourly' : frequency;
}

// Reverse of toApiEmailFrequency: normalize a backend cadence value back to its UI frequency.
// Null/empty-safe so optional params map to '' rather than throwing.
export function fromApiEmailFrequency(value?: string): string {
  const lower = (value ?? '').toLowerCase();
  return lower === 'hourly' ? 'instant' : lower;
}

export type SavedSearchAreaBounds = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
};

type BuildSavedSearchPayloadParams = {
  name: string;
  email: string;
  frequency: SavedSearchFrequency;
  resourceId: string;
  areaId: string;
  searchSlug: string;
  filters: FilterState;
  areaBounds: SavedSearchAreaBounds;
  /** The area's typed Zillow id; adds the matching typed field to `filter`. */
  searchScope?: HpSearchScopeDto;
};

type MatchingSavedSearchParams = {
  resourceId: string;
  areaId: string;
  searchSlug: string;
  filters: FilterState;
  /** Current area's typed Zillow id; lets a saved search match by its stored typed-id field when legacy ids diverge. */
  searchScope?: HpSearchScopeDto;
};

const DEFAULT_SAVED_SEARCH_SLUG = 'apartments-for-rent';
const DEFAULT_SAVED_SEARCH_BEDROOMS = '0,1,2,3,4,5,6,7,8plus';
const DEFAULT_SAVED_SEARCH_BATHROOMS = '0,0.5,1,1.5,2,2.5,3,3.5,4,4.5,5,5.5,6,6.5,7,7.5,8plus';
const DEFAULT_SAVED_SEARCH_PROPERTY_TYPES = 'condo,divided,garden,house,land,large,medium,townhouse';
const DEFAULT_SAVED_SEARCH_LISTING_TYPES = 'rental,room,sublet,corporate';
const DEFAULT_SAVED_SEARCH_VISIBLE = 'favorite,inquiry,new,note,notified,viewed';
const ALL_USER_VISIBLE_API_PROPERTY_TYPES = ['condo', 'divided', 'garden', 'house', 'large', 'medium', 'townhouse'];
const KNOWN_SAVED_SEARCH_SLUGS = new Set([
  'apartments-for-rent',
  'houses-for-rent',
  'condos-for-rent',
  'townhomes-for-rent',
  'rooms-for-rent',
]);

// Static filter defaults shared by the saved-search and recent-search bodies.
const BASE_SEARCH_API_FILTER = {
  bedrooms: DEFAULT_SAVED_SEARCH_BEDROOMS,
  bathrooms: DEFAULT_SAVED_SEARCH_BATHROOMS,
  propertyTypes: DEFAULT_SAVED_SEARCH_PROPERTY_TYPES,
  listingTypes: DEFAULT_SAVED_SEARCH_LISTING_TYPES,
  amenities: '',
  channels: '',
  components: 'basic,useritem,quality,model,photos',
  includePhotosCollection: true,
  keywords: '',
  laundry: '',
  pets: '',
  limit: LISTINGS_PER_PAGE,
  offset: 0,
  visible: DEFAULT_SAVED_SEARCH_VISIBLE,
} as const;

const TRANSIENT_FILTER_KEYS = new Set([
  'areas',
  'minLat',
  'maxLat',
  'minLon',
  'maxLon',
  'orderBy',
]);

const NON_COMPARABLE_FILTER_KEYS = new Set([
  'searchSlug',
  'searchTitle',
  'visible',
  'includePhotosCollection',
  'components',
  'limit',
  'offset',
  'channels',
  'lat',
  'lon',
  'orderBy',
]);

function normalizeComparableValue(value: unknown): string {
  if (Array.isArray(value)) {
    return [...value].map(String).sort().join(',');
  }

  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
}

function isTruthyFilterValue(value: unknown): boolean {
  return value === true || value === 'true';
}

function sortDelimitedValues(value: string): string {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .sort()
    .join('-');
}

function normalizeDelimitedValues(value: unknown): string {
  return String(value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .sort()
    .join(',');
}

function buildBoundedRangeQueryValue(min: unknown, max: unknown): string | null {
  const normalizedMin = normalizeComparableValue(min) === '0' ? '' : normalizeComparableValue(min);
  const normalizedMax = normalizeComparableValue(max);

  if (!normalizedMin && !normalizedMax) {
    return null;
  }

  return `${normalizedMin}-${normalizedMax}`;
}

function bedroomsFilterToQueryValue(value: unknown): string | null {
  const normalized = normalizeComparableValue(value);
  if (!normalized || normalized === DEFAULT_SAVED_SEARCH_BEDROOMS) {
    return null;
  }

  const values = normalized.split(',').map((item) => item.trim()).filter(Boolean);
  if (values.length === 0) return null;
  if (values.length === 1) return values[0];
  if (values[0] === '0') {
    return null;
  }
  return `${values[0]}-8plus`;
}

function bathroomsFilterToQueryValue(value: unknown): string | null {
  const normalized = normalizeComparableValue(value);
  if (!normalized || normalized === DEFAULT_SAVED_SEARCH_BATHROOMS) {
    return null;
  }

  const values = normalized.split(',').map((item) => item.trim()).filter(Boolean);
  if (values.length === 0) return null;
  if (values[0] === '0') return null;
  return values[0];
}

function propertyTypesFilterToQueryValue(value: unknown, searchSlug: string): string | null {
  const normalized = normalizeDelimitedValues(value);
  if (!normalized || normalized === normalizeDelimitedValues(DEFAULT_SAVED_SEARCH_PROPERTY_TYPES)) {
    return null;
  }

  const normalizedValues = normalized.split(',').map((item) => item.trim()).filter(Boolean);
  const normalizedValuesWithoutLand = normalizedValues.filter((item) => item !== 'land');
  const normalizedWithoutLand = normalizedValuesWithoutLand.join(',');
  const apiValues = new Set(normalizedValuesWithoutLand);
  const hasApartmentTypes = apiValues.has('apartment') || apiValues.has('garden') || apiValues.has('large') || apiValues.has('medium');
  const hasAllUserVisibleTypes =
    hasApartmentTypes &&
    ['condo', 'divided', 'house', 'townhouse'].every((type) => apiValues.has(type));
  const hasLegacyAllUserVisibleTypes =
    ALL_USER_VISIBLE_API_PROPERTY_TYPES.every((type) => apiValues.has(type));

  if (hasAllUserVisibleTypes || hasLegacyAllUserVisibleTypes) {
    return null;
  }

  if (
    (searchSlug === 'apartments-for-rent' &&
      (normalizedWithoutLand === 'apartment' || normalizedWithoutLand === 'garden,large,medium')) ||
    (searchSlug === 'houses-for-rent' && normalizedWithoutLand === 'house') ||
    (searchSlug === 'condos-for-rent' && normalizedWithoutLand === 'condo') ||
    (searchSlug === 'townhomes-for-rent' && normalizedWithoutLand === 'townhouse')
  ) {
    return null;
  }

  const propertyTypes = new Set<string>();

  if (apiValues.has('garden') || apiValues.has('large') || apiValues.has('medium')) {
    propertyTypes.add('apartment');
  }
  if (apiValues.has('condo')) {
    propertyTypes.add('condo');
  }
  if (apiValues.has('divided')) {
    propertyTypes.add('duplex');
  }
  if (apiValues.has('house')) {
    propertyTypes.add('house');
  }
  if (apiValues.has('townhouse')) {
    propertyTypes.add('townhouse');
  }

  if (propertyTypes.size === 0) {
    return null;
  }

  return [...propertyTypes].sort().join('-');
}

function listingTypesFilterToQueryValue(value: unknown, searchSlug: string): string | null {
  const normalized = normalizeDelimitedValues(value);
  if (!normalized || normalized === normalizeDelimitedValues(DEFAULT_SAVED_SEARCH_LISTING_TYPES)) {
    return null;
  }

  if (
    (searchSlug === 'apartments-for-rent' || searchSlug === 'houses-for-rent') &&
    normalized === 'rental'
  ) {
    return null;
  }

  if (searchSlug === 'rooms-for-rent' && normalized === 'room') {
    return null;
  }

  return sortDelimitedValues(normalized);
}

function createdWithinFilterToQueryValue(value: unknown): string | null {
  const normalized = normalizeComparableValue(value);

  switch (normalized) {
    case '1':
    case 'hour':
      return 'hour';
    case '24':
    case 'day':
      return 'day';
    case '168':
    case 'week':
      return 'week';
    case '720':
    case 'month':
      return 'month';
    default:
      return null;
  }
}

function restrictionsFilterToQueryValue(filter: SavedSearchFilter): string | null {
  if (isTruthyFilterValue(filter.incomeRestricted)) return 'income';
  if (isTruthyFilterValue(filter.seniorHousing)) return 'senior';
  if (isTruthyFilterValue(filter.studentHousing)) return 'student';
  if (isTruthyFilterValue(filter.militaryHousing)) return 'military';
  return null;
}

function additionalOptsFilterToQueryValue(filter: SavedSearchFilter): string | null {
  const options: Array<string> = [];

  if (normalizeComparableValue(filter.minPhotos) === '1') {
    options.push('requiresPhotos');
  }
  if (filter.includeVaguePricing === false || filter.includeVaguePricing === 'false') {
    options.push('requiresPrice');
  }
  if (isTruthyFilterValue(filter.isListedByOwner)) {
    options.push('forRentByOwner');
  }
  if (isTruthyFilterValue(filter.hasSpecialOffers)) {
    options.push('hasOffers');
  }
  if (isTruthyFilterValue(filter.isAcceptingRentalApplications)) {
    options.push('acceptingApplications');
  }

  if (options.length === 0) {
    return null;
  }

  return options.sort().join('-');
}

export function savedSearchFilterToQuery(filter: SavedSearchFilter, searchSlug: string): Record<string, string> {
  const query: Record<string, string> = {};

  const price = buildBoundedRangeQueryValue(filter.lowPrice, filter.highPrice);
  if (price) {
    query.price = price;
  }

  const beds = bedroomsFilterToQueryValue(filter.bedrooms);
  if (beds) query.beds = beds;

  const baths = bathroomsFilterToQueryValue(filter.bathrooms);
  if (baths) query.baths = baths;

  const pets = normalizeComparableValue(filter.pets);
  if (pets) query.pets = sortDelimitedValues(pets);

  const laundry = normalizeComparableValue(filter.laundry);
  if (laundry) query.laundry = sortDelimitedValues(laundry);

  const amenities = normalizeComparableValue(filter.amenities);
  if (amenities) query.amenities = sortDelimitedValues(amenities);

  if (isTruthyFilterValue(filter.furnished)) {
    query.furnished = 'true';
  }

  const propertyTypes = propertyTypesFilterToQueryValue(filter.propertyTypes, searchSlug);
  if (propertyTypes) query.propertyTypes = propertyTypes;

  const sqft = buildBoundedRangeQueryValue(filter.minSqft, filter.maxSqft);
  if (sqft) {
    query.sqft = sqft;
  }

  const createdWithin = createdWithinFilterToQueryValue(filter.maxCreated);
  if (createdWithin) query.createdWithin = createdWithin;

  const rentalTypes = listingTypesFilterToQueryValue(filter.listingTypes, searchSlug);
  if (rentalTypes) query.rentalTypes = rentalTypes;

  const restrictions = restrictionsFilterToQueryValue(filter);
  if (restrictions) query.restrictions = restrictions;

  const startOfAvailabilityDate = normalizeComparableValue(filter.startOfAvailabilityDate);
  const endOfAvailabilityDate = normalizeComparableValue(filter.endOfAvailabilityDate);
  const moveInBy = endOfAvailabilityDate || startOfAvailabilityDate;
  if (moveInBy) {
    query.avail = `to${moveInBy}`;
  }

  const keywords = normalizeComparableValue(filter.keywords).trim();
  if (keywords) query.keywords = keywords;

  const additionalOpts = additionalOptsFilterToQueryValue(filter);
  if (additionalOpts) query.additionalOpts = additionalOpts;

  return query;
}

function serializeComparableQuery(query: Record<string, string>): string {
  return JSON.stringify(
    Object.entries(query).sort(([a], [b]) => a.localeCompare(b)),
  );
}

function withoutTransientFilterKeys(
  filter: SavedSearchFilter | Record<string, unknown>,
): SavedSearchFilter {
  const normalized: SavedSearchFilter = {};

  for (const [key, value] of Object.entries(filter)) {
    if (TRANSIENT_FILTER_KEYS.has(key) || NON_COMPARABLE_FILTER_KEYS.has(key)) continue;
    if (value === '') continue;
    const normalizedValue = normalizeComparableValue(value);
    if (!normalizedValue) continue;
    if (key === 'bedrooms' && normalizedValue === DEFAULT_SAVED_SEARCH_BEDROOMS) continue;
    if (key === 'bathrooms' && normalizedValue === DEFAULT_SAVED_SEARCH_BATHROOMS) continue;
    if (key === 'propertyTypes' && normalizedValue === DEFAULT_SAVED_SEARCH_PROPERTY_TYPES) continue;
    if (key === 'listingTypes' && normalizedValue === DEFAULT_SAVED_SEARCH_LISTING_TYPES) continue;
    normalized[key] = normalizedValue;
  }

  return normalized;
}

function getSavedSearchPropertyTypeSlug(propertyTypes: string): string | null {
  switch (normalizeDelimitedValues(propertyTypes)) {
    case 'garden,large,medium':
      return 'apartments-for-rent';
    case 'house':
      return 'houses-for-rent';
    case 'condo':
      return 'condos-for-rent';
    case 'townhouse':
      return 'townhomes-for-rent';
    default:
      return null;
  }
}

function getSavedSearchTitle(searchSlug: string): string {
  switch (searchSlug) {
    case 'apartments-for-rent':
      return 'Apartments for Rent';
    case 'houses-for-rent':
      return 'Houses for Rent';
    case 'condos-for-rent':
      return 'Condos for Rent';
    case 'townhomes-for-rent':
      return 'Townhomes for Rent';
    case 'rooms-for-rent':
      return 'Rooms for Rent';
    default:
      return 'Apartments for Rent';
  }
}

function getSavedSearchSlugFromTitle(searchTitle: string): string | null {
  switch (searchTitle.trim()) {
    case 'Apartments for Rent':
      return 'apartments-for-rent';
    case 'Houses for Rent':
      return 'houses-for-rent';
    case 'Condos for Rent':
      return 'condos-for-rent';
    case 'Townhomes for Rent':
      return 'townhomes-for-rent';
    case 'Rooms for Rent':
      return 'rooms-for-rent';
    default:
      return null;
  }
}

function isKnownSavedSearchSlug(value: string): boolean {
  return KNOWN_SAVED_SEARCH_SLUGS.has(value);
}

function getSavedSearchAreaId(search: SavedSearchItem): string | null {
  if (typeof search.borderAreaId === 'string' && search.borderAreaId.trim()) {
    return search.borderAreaId.trim();
  }

  if (typeof search.borderAreaId === 'number' && Number.isFinite(search.borderAreaId)) {
    return String(search.borderAreaId);
  }

  const areas = search.filter?.areas;
  if (Array.isArray(areas)) {
    const firstArea = areas
      .map((area) => {
        if (typeof area === 'object' && area !== null && 'id' in area) {
          const id = (area as { id?: unknown }).id;
          return String(id ?? '').trim();
        }

        return String(area).trim();
      })
      .find(Boolean);
    return firstArea || null;
  }

  if (typeof areas === 'string' && areas.trim()) {
    return areas.split(',')[0]?.trim() || null;
  }

  if (typeof areas === 'number') {
    return String(areas);
  }

  if (typeof areas === 'object' && areas !== null && 'id' in areas) {
    const id = (areas as { id?: unknown }).id;
    return String(id ?? '').trim() || null;
  }

  return null;
}

// The typed id the BE stored on the saved search under the field matching the
// current scope's type; tolerates the number or stringified-number
// forms it may echo back.
function getSavedSearchScopeId(search: SavedSearchItem, scope: HpSearchScopeDto | undefined): number | null {
  if (!scope) {
    return null;
  }
  const raw = search.filter?.[zillowIdFilterField(scope.type)];
  if (raw == null || raw === '') {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function buildComparableSavedSearchFilter(filters: FilterState): SavedSearchFilter {
  return withoutTransientFilterKeys(
    toApiParams({
      ...filters,
      orderBy: null,
      // Saved searches never carry commute (matching legacy alerts) — it's a
      // view-time isochrone filter, not part of the alert criteria.
      commute: null,
    }),
  );
}

export function buildSavedSearchMatchKey({
  resourceId,
  areaId,
  searchSlug,
  filters,
}: MatchingSavedSearchParams): string {
  return JSON.stringify({
    resourceId,
    areaId,
    searchSlug,
    query: serializeToUrl({
      ...filters,
      orderBy: null,
    }),
  });
}

export function getSavedSearchSlug(search: SavedSearchItem): string {
  const explicitSlug = search.searchFilterType?.trim();
  if (explicitSlug && isKnownSavedSearchSlug(explicitSlug)) {
    return explicitSlug;
  }

  const nestedFilterSlug =
    typeof search.filter?.search === 'object' &&
    search.filter?.search !== null &&
    typeof search.filter.search.slug === 'string'
      ? search.filter.search.slug.trim()
      : '';
  if (nestedFilterSlug) {
    return nestedFilterSlug;
  }

  const filterSlug = typeof search.filter?.searchSlug === 'string' ? search.filter.searchSlug.trim() : '';
  if (filterSlug) {
    return filterSlug;
  }

  const nestedFilterTitle =
    typeof search.filter?.search === 'object' &&
    search.filter?.search !== null &&
    typeof search.filter.search.title === 'string'
      ? search.filter.search.title.trim()
      : '';
  const slugFromNestedTitle = nestedFilterTitle ? getSavedSearchSlugFromTitle(nestedFilterTitle) : null;
  if (slugFromNestedTitle) {
    return slugFromNestedTitle;
  }

  const filterTitle = typeof search.filter?.searchTitle === 'string' ? search.filter.searchTitle.trim() : '';
  const slugFromTitle = filterTitle ? getSavedSearchSlugFromTitle(filterTitle) : null;
  if (slugFromTitle) {
    return slugFromTitle;
  }

  const listingTypes = String(search.filter?.listingTypes ?? '');
  const listingTypeValues = listingTypes
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  if (listingTypeValues.length === 1 && listingTypeValues[0] === 'room') {
    return 'rooms-for-rent';
  }

  const propertyTypes = String(search.filter?.propertyTypes ?? '');
  const propertyTypeSlug = getSavedSearchPropertyTypeSlug(propertyTypes);
  if (propertyTypeSlug) {
    return propertyTypeSlug;
  }

  return DEFAULT_SAVED_SEARCH_SLUG;
}

export function getSavedSearchId(search: SavedSearchItem): string | null {
  return search.sid ?? search.id ?? search.searchId ?? null;
}

export function savedSearchToFilterState(search: SavedSearchItem): FilterState {
  // SRP slugs do not apply filters, so retain even the types that match the slug.
  return deserializeFromUrl(savedSearchFilterToQuery(search.filter ?? {}, ''));
}

export function buildSavedSearchViewUrl(search: SavedSearchItem): string {
  if (!search.resourceId) {
    return '/';
  }

  const query = new URLSearchParams(serializeToUrl(savedSearchToFilterState(search))).toString();
  const path = `/${search.resourceId}/${getSavedSearchSlug(search)}`;
  return query ? `${path}?${query}` : path;
}

export function buildSavedSearchPayload({
  name,
  email,
  frequency,
  areaId,
  searchSlug,
  filters,
  areaBounds,
  searchScope,
}: BuildSavedSearchPayloadParams): SavedSearchCreateBody {
  const apiFilter = toApiParams({
    ...filters,
    orderBy: filters.orderBy === null ? null : filters.orderBy,
    // Saved searches never carry commute — see buildComparableSavedSearchFilter.
    commute: null,
  });

  return {
    name,
    toEmail: email,
    emailFrequency: toApiEmailFrequency(frequency),
    sid: null,
    filter: {
      ...BASE_SEARCH_API_FILTER,
      lat: '',
      lon: '',
      orderBy: filters.orderBy ?? 'score',
      searchSlug,
      searchTitle: getSavedSearchTitle(searchSlug),
      ...apiFilter,
      areas: areaId,
      ...toZillowIdFilterFields(searchScope),
      minLat: areaBounds.minLat,
      maxLat: areaBounds.maxLat,
      minLon: areaBounds.minLon,
      maxLon: areaBounds.maxLon,
    },
  };
}

export type RecentSearchGeo = {
  // null when searching by map viewport (border off) — `areas` is omitted then, matching legacy.
  areaId: string | null;
  lat?: number;
  lon?: number;
  bounds: SavedSearchAreaBounds;
  /** The area's typed Zillow id. Sent only alongside `areas`. */
  searchScope?: HpSearchScopeDto;
};

export function getAreaBounds(area?: {
  minLat?: number;
  maxLat?: number;
  minLon?: number;
  maxLon?: number;
}): SavedSearchAreaBounds | null {
  if (
    area?.minLat !== undefined &&
    area.maxLat !== undefined &&
    area.minLon !== undefined &&
    area.maxLon !== undefined
  ) {
    return { minLat: area.minLat, maxLat: area.maxLat, minLon: area.minLon, maxLon: area.maxLon };
  }

  return null;
}

type BuildRecentSearchBodyParams = {
  filters: FilterState;
  searchSlug: string;
  geo: RecentSearchGeo;
};

// The /v2/user endpoints expect Java param names for additional opts (see legacy-api.md).
function remapAdditionalOptsToJavaParams(
  apiFilter: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> {
  const { requiresPhotos, requiresPrice, forRentByOwner, hasOffers, acceptingApplications, ...rest } = apiFilter;
  return {
    ...rest,
    ...(requiresPhotos === true && { minPhotos: 1 }),
    ...(requiresPrice === true && { includeVaguePricing: false }),
    ...(forRentByOwner === true && { isListedByOwner: true }),
    ...(hasOffers === true && { hasSpecialOffers: true }),
    ...(acceptingApplications === true && { isAcceptingRentalApplications: true }),
  };
}

function getRecentSearchTitle(searchSlug: string): string {
  if (KNOWN_SAVED_SEARCH_SLUGS.has(searchSlug)) {
    return getSavedSearchTitle(searchSlug);
  }
  return searchSlug
    .split('-')
    .filter(Boolean)
    .map((word) => (word === 'for' ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
}

export function buildRecentSearchBody({ filters, searchSlug, geo }: BuildRecentSearchBodyParams): RecentSearchUpdateBody {
  // Unlike saved searches, recent search keeps commute params (legacy parity).
  const apiFilter = remapAdditionalOptsToJavaParams(toApiParams(filters));

  return {
    filter: {
      ...BASE_SEARCH_API_FILTER,
      orderBy: filters.orderBy ?? 'score',
      searchSlug,
      searchTitle: getRecentSearchTitle(searchSlug),
      ...apiFilter,
      ...(geo.areaId !== null && { areas: geo.areaId }),
      ...(geo.areaId !== null ? toZillowIdFilterFields(geo.searchScope) : {}),
      ...(geo.lat !== undefined && { lat: geo.lat }),
      ...(geo.lon !== undefined && { lon: geo.lon }),
      minLat: geo.bounds.minLat,
      maxLat: geo.bounds.maxLat,
      minLon: geo.bounds.minLon,
      maxLon: geo.bounds.maxLon,
    },
  };
}

export function findMatchingSavedSearch(
  searches: Array<SavedSearchItem> | undefined,
  { resourceId, areaId, searchSlug, filters, searchScope }: MatchingSavedSearchParams,
): SavedSearchItem | null {
  if (!searches || searches.length === 0) {
    return null;
  }

  const comparableCurrentQuery = serializeComparableQuery(
    serializeToUrl({
      ...filters,
      orderBy: null,
    }),
  );

  return (
    searches.find((search) => {
      const savedSearchSlug = getSavedSearchSlug(search);
      const savedResourceId = search.resourceId?.trim();
      const savedAreaId = getSavedSearchAreaId(search);
      const savedScopeId = getSavedSearchScopeId(search, searchScope);
      const resourceMatches = savedResourceId ? savedResourceId === resourceId : null;
      // A search saved with a typed Zillow id (region, school, or district)
      // still matches by it once the legacy areas id is gone or diverges.
      const areaMatches =
        savedAreaId || savedScopeId != null
          ? savedAreaId === areaId || (savedScopeId != null && savedScopeId === searchScope?.id)
          : null;

      if (resourceMatches === false && areaMatches === false) {
        return false;
      }

      if (resourceMatches === false && areaMatches === null) {
        return false;
      }

      if (areaMatches === false && resourceMatches === null) {
        return false;
      }

      if (savedSearchSlug !== searchSlug) {
        return false;
      }

      const comparableSavedQuery = serializeComparableQuery(
        savedSearchFilterToQuery(search.filter ?? {}, savedSearchSlug),
      );

      return comparableSavedQuery === comparableCurrentQuery;
    }) ?? null
  );
}
